import React, { useState } from 'react';
import { Card, Col, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap-5';

import OffMarketPropertyImage from './results/off_market_property_image';
import OffMarketCompSummary from './results/off_market_comp_summary';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuildingMemo, faCircleXmark, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { connect } from 'react-redux';
import { setOffMarketPropertyId } from 'bundles/connect/components/store/actions/off_market_property';
import { setOffMarketPropertyId as setOffMarketPropertyIdTurbo } from '../helpers/navigation_helper';
import type { InsightsProperty } from 'bundles/connect/types';
import EmbeddedMode from '../../embedded_mode';
import { syncCompToRethink } from 'bundles/connect/helpers/prospect_api';
import { displayToast } from 'bundles/connect/helpers/display_toast';

type OffMarketCompDetailsCardProps = {
  fromTurbo?: boolean;
  property: InsightsProperty;
  setOffMarketPropertyId: (id: string) => void;
  onRemoval: (id: string) => void;
}

function OffMarketCompDetailsCard(
  { fromTurbo = false, property, setOffMarketPropertyId, onRemoval }: OffMarketCompDetailsCardProps
) {
  const [disableSyncCompButton, setDisableSyncCompButton] = useState(false);
  const [loading, setLoading] = useState(false);

  const handlePropertyClick = (id) => {
    setOffMarketPropertyId(id);
    if (fromTurbo) {
      setOffMarketPropertyIdTurbo(id);
    }
  };

  const handleRemoval = (e) => {
    e.stopPropagation();
    onRemoval(property.propertyId);
  };

  const handleAddCompToCrm = (e) => {
    e.stopPropagation();
    if (disableSyncCompButton) {
      return;
    }

    setLoading(true);
    setDisableSyncCompButton(true);
    syncCompToRethink(property.propertyId).then(() => {
      displayToast('Comp property synced to successfully', 'success');
    }).catch(() => {
      displayToast('There was an error syncing the comp property', 'error');
      setDisableSyncCompButton(false);
    }).finally(() => {
      setLoading(false);
    });
  };

  return (
    <Card className="h-100 clickable shadow" onClick={() => handlePropertyClick(property.propertyId)}>
      <Row className="g-0 align-items-stretch h-100">
        <Col className="d-flex flex-column">
          <Card.Body className="p-2">
            <EmbeddedMode>
              <div
                className={
                  `d-flex
                bg-light
                rounded-circle
                clickable
                position-absolute
                justify-content-center
                align-items-center
                z-3`
                }
                style={{ width: 24, height: 24, top: 5, right: 5 }}
                onClick={handleRemoval}
              >
                <FontAwesomeIcon icon={faTimes} />
              </div>
            </EmbeddedMode>
            <OffMarketCompSummary property={property} />
          </Card.Body>
        </Col>
        <Col className="map-result-thumb off-market-result-thumb d-none d-md-block p-2" md="auto">
          <OffMarketPropertyImage result={property} />
        </Col>
        <EmbeddedMode display forRethink>
          <Col
            className="d-flex flex-column border-start"
            xs={{ order: 'last', span: 'auto' }}
          >
            <div className="flex-fill clickable border-bottom">
              <OverlayTrigger
                overlay={
                  <Tooltip id={`remove-comp-tooltip-${property.propertyId}`}>
                    Remove From Comparables
                  </Tooltip>
                }
                placement="top"
              >
                <div className="align-content-center text-center p-2 h-100" onClick={handleRemoval}>
                  <FontAwesomeIcon
                    className="text-accent clickable"
                    icon={faCircleXmark}
                  />
                </div>
              </OverlayTrigger>
            </div>
            <div className="flex-fill clickable">
              {loading ?
                <div className="align-content-center text-center p-2 h-100">
                  <Spinner />
                </div> :
                <OverlayTrigger
                  overlay={
                    <Tooltip id={`add-comp-to-crm-tooltip-${property.propertyId}`}>
                      {disableSyncCompButton ? 'Comp already saved' : 'Save Comp Property'}
                    </Tooltip>
                  }
                  placement="top"
                >
                  <div className="align-content-center text-center p-2 h-100" onClick={handleAddCompToCrm}>
                    <FontAwesomeIcon
                      className={`${disableSyncCompButton ? 'text-disabled' : 'text-accent'} clickable`}
                      icon={faBuildingMemo}
                    />
                  </div>
                </OverlayTrigger>
              }
            </div>
          </Col>
        </EmbeddedMode>
      </Row>
    </Card>
  );
}

export default connect(undefined, { setOffMarketPropertyId })(OffMarketCompDetailsCard);
